/**
 * Security Findings Hook using React Query
 * 
 * Handles security findings data fetching with pagination and filters
 */

import { useQuery } from '@tanstack/react-query';
import { 
  getSecurityFindings,
  type SecurityFindingsResponse
} from '@/services/security-scanner';

/**
 * Query key factory for security findings
 */
export const securityFindingsKeys = {
  all: ['securityFindings'] as const,
  lists: () => [...securityFindingsKeys.all, 'list'] as const,
  list: (params: {
    org_id?: string | number;
    user_id?: string | number;
    page?: number;
    pageSize?: number;
    severity?: string;
    category?: string;
    metadata_type?: string;
    search?: string;
  }) => [...securityFindingsKeys.lists(), params] as const,
};

/**
 * Hook to fetch security findings with pagination and filters
 * 
 * @example
 * const { data, isLoading, error } = useSecurityFindings({
 *   org_id: 19,
 *   user_id: 14,
 *   page: 1,
 *   pageSize: 10,
 *   severity: 'High',
 *   search: 'CRUD'
 * });
 * // data.findings - Array of finding objects
 * // data.pagination - Pagination info
 * // data.summary - Summary with filters and totals
 */
export function useSecurityFindings(params?: {
  org_id?: number | string;
  user_id?: number | string;
  page?: number;
  pageSize?: number;
  severity?: string;
  category?: string;
  metadata_type?: string;
  search?: string;
}) {
  return useQuery<SecurityFindingsResponse>({
    queryKey: securityFindingsKeys.list({
      org_id: params?.org_id || '',
      user_id: params?.user_id || '',
      page: params?.page,
      pageSize: params?.pageSize,
      severity: params?.severity,
      category: params?.category,
      metadata_type: params?.metadata_type,
      search: params?.search,
    }),
    queryFn: () => getSecurityFindings({
      org_id: params!.org_id!,
      user_id: params!.user_id!,
      page: params?.page,
      pageSize: params?.pageSize,
      severity: params?.severity,
      category: params?.category,
      metadata_type: params?.metadata_type,
      search: params?.search,
    }),
    enabled: !!params?.org_id && !!params?.user_id, // Only fetch if both IDs are provided
    staleTime: 1000 * 60 * 2, // 2 minutes - findings may change frequently
    placeholderData: (previousData) => previousData, // Keep previous data while loading new page
  });
}
